// Admin data layer for Children.

import { supabase, isSupabaseConfigured } from "../supabase";

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

// New fields added in Module 42. Wrapped in a runtime probe so the page
// keeps working pre-migration: if PostgREST 400s on the unknown column,
// we fall back to the legacy SELECT and synthesize default values.
const NEW_CHILD_FIELDS = "schedule_pattern, tuition_multiplier";
const PROFILE_PHOTO_FIELD = "profile_photo_path";

function isUnknownColumnError(err) {
  const msg = (err?.message || "") + " " + (err?.details || "");
  return /column .* does not exist/i.test(msg) || err?.code === "42703";
}

function withScheduleDefaults(rows) {
  return (rows || []).map((r) => ({
    schedule_pattern: r.schedule_pattern || "full_time",
    tuition_multiplier: r.tuition_multiplier ?? 1.0,
    profile_photo_path: r.profile_photo_path ?? null,
    ...r,
  }));
}

export async function listChildren({ search, classroomId, status } = {}) {
  ensureLive();
  const baseCols = `
    id, first_name, last_name, date_of_birth, allergies, medical_notes,
    enrolled_at, withdrawn_at, classroom_id, family_id,
    classroom:classrooms(id, name, age_group),
    family:families(id, display_name)
  `;
  const fullCols = `${baseCols.trim()},\n    ${NEW_CHILD_FIELDS},\n    ${PROFILE_PHOTO_FIELD}`;
  const midCols = `${baseCols.trim()},\n    ${NEW_CHILD_FIELDS}`;

  const fetchWith = async (cols) => {
    let q = supabase
      .from("children")
      .select(cols)
      .order("first_name", { ascending: true });
    if (classroomId) q = q.eq("classroom_id", classroomId);
    if (status === "active") q = q.is("withdrawn_at", null);
    if (status === "withdrawn") q = q.not("withdrawn_at", "is", null);
    return q;
  };

  let { data, error } = await fetchWith(fullCols);
  if (error && isUnknownColumnError(error)) {
    ({ data, error } = await fetchWith(midCols));
  }
  if (error && isUnknownColumnError(error)) {
    ({ data, error } = await fetchWith(baseCols));
  }
  if (error) throw error;
  let rows = withScheduleDefaults(data);
  if (search) {
    const s = search.toLowerCase();
    rows = rows.filter((c) => {
      const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
      return name.includes(s);
    });
  }
  return rows;
}

export async function getChild(id) {
  ensureLive();
  const baseCols = `
    id, organization_id, first_name, last_name, date_of_birth, allergies,
    medical_notes, enrolled_at, withdrawn_at, classroom_id, family_id,
    classroom:classrooms(id, name, age_group),
    family:families(id, display_name)
  `;
  const fullCols = `${baseCols.trim()},\n    ${NEW_CHILD_FIELDS},\n    ${PROFILE_PHOTO_FIELD}`;
  const midCols = `${baseCols.trim()},\n    ${NEW_CHILD_FIELDS}`;

  let { data, error } = await supabase
    .from("children")
    .select(fullCols)
    .eq("id", id)
    .maybeSingle();
  if (error && isUnknownColumnError(error)) {
    ({ data, error } = await supabase
      .from("children")
      .select(midCols)
      .eq("id", id)
      .maybeSingle());
  }
  if (error && isUnknownColumnError(error)) {
    ({ data, error } = await supabase
      .from("children")
      .select(baseCols)
      .eq("id", id)
      .maybeSingle());
  }
  if (error) throw error;
  if (!data) return null;
  return withScheduleDefaults([data])[0];
}

export async function createChild(patch) {
  ensureLive();
  // Try with the new schedule fields, fall back without them if the
  // migrations haven't been applied yet (development sandbox safety net).
  const tryInsert = async (p) =>
    supabase.from("children").insert(p).select("*").single();
  let { data, error } = await tryInsert(patch);
  if (error && isUnknownColumnError(error)) {
    const stripped = { ...patch };
    delete stripped.schedule_pattern;
    delete stripped.tuition_multiplier;
    ({ data, error } = await tryInsert(stripped));
  }
  if (error) throw error;
  return data;
}

export async function updateChild(id, patch) {
  ensureLive();
  const tryUpdate = async (p) =>
    supabase.from("children").update(p).eq("id", id).select("*").single();
  let { data, error } = await tryUpdate(patch);
  if (error && isUnknownColumnError(error)) {
    const stripped = { ...patch };
    delete stripped.schedule_pattern;
    delete stripped.tuition_multiplier;
    ({ data, error } = await tryUpdate(stripped));
  }
  if (error) throw error;
  return data;
}

export async function withdrawChild(id, date) {
  return updateChild(id, { withdrawn_at: date || new Date().toISOString().slice(0, 10) });
}

export async function reinstateChild(id) {
  return updateChild(id, { withdrawn_at: null });
}

export function computeAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - dob.getFullYear();
  const mdiff = now.getMonth() - dob.getMonth();
  if (mdiff < 0 || (mdiff === 0 && now.getDate() < dob.getDate())) years -= 1;
  if (years < 2) {
    const months =
      (now.getFullYear() - dob.getFullYear()) * 12 +
      (now.getMonth() - dob.getMonth()) -
      (now.getDate() < dob.getDate() ? 1 : 0);
    return { label: `${Math.max(0, months)} mo`, months };
  }
  return { label: `${years} yr`, months: years * 12 };
}

// ---------------------------------------------------------------------------
// Schedule patterns — UI presets that auto-fill tuition_multiplier.
// "custom" lets admins type any 0..1 multiplier without it snapping.
// ---------------------------------------------------------------------------
export const SCHEDULE_PATTERNS = [
  { value: "full_time",      label: "Full-time (5-day)", short: "Full-time", multiplier: 1.000 },
  { value: "part_time_4day", label: "Part-time · 4-day",  short: "4-day",     multiplier: 0.800 },
  { value: "part_time_3day", label: "Part-time · 3-day",  short: "3-day",     multiplier: 0.600 },
  { value: "part_time_2day", label: "Part-time · 2-day",  short: "2-day",     multiplier: 0.400 },
  { value: "custom",         label: "Custom",             short: "Custom",    multiplier: null  },
];

export const SCHEDULE_LABELS = Object.fromEntries(
  SCHEDULE_PATTERNS.map((p) => [p.value, p.label])
);
export const SCHEDULE_SHORT = Object.fromEntries(
  SCHEDULE_PATTERNS.map((p) => [p.value, p.short])
);

export function multiplierForPattern(pattern) {
  return SCHEDULE_PATTERNS.find((p) => p.value === pattern)?.multiplier ?? null;
}
